"use client";

import React from "react";
import { createPortal } from "react-dom";
import { ActionMenuProps } from "./types";
import { cn } from "utils/utils";

export function CardActionsMenu({
  actions,
  isOpen,
  position,
  onClose,
  menuRef,
  title,
}: ActionMenuProps) {
  // Closed menus stay unmounted: one of these portals per card tile, each a
  // fixed full-time compositing layer in WebKit, is what janked the binder
  // grid. The enter animation runs via CSS on mount instead.
  if (!isOpen) return null;

  return createPortal(
    <div
      className="animate-in fade-in zoom-in-95 duration-150 ease-out"
      style={{
        position: "fixed",
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 9999,
      }}
    >
      <ul
        ref={menuRef}
        className="menu menu-sm bg-base-100 rounded-box border-base-content/10 w-52 space-y-1 border p-2 shadow-xl"
        role="menu"
        aria-label="Card actions menu"
      >
        {actions.map((action) => (
          <li key={action.id} role="presentation">
            <button
              role="menuitem"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                action.handler();
                onClose();
              }}
              disabled={action.disabled}
              className={cn(
                "flex w-full items-center gap-3 px-4 py-2 text-sm transition-colors",
                {
                  "text-primary":
                    action.variant === "primary" || !action.variant,
                  "text-secondary": action.variant === "secondary",
                  "text-error": action.variant === "error",
                  "text-success": action.variant === "success",
                },
                action.disabled && "cursor-not-allowed opacity-50"
              )}
            >
              {action.loading ? (
                <span className="loading loading-spinner loading-xs"></span>
              ) : (
                action.icon
              )}
              {action.label}
            </button>
          </li>
        ))}
      </ul>
    </div>,
    document.body
  );
}
